import React from 'react';
import type { AppRouter } from '@/types/appRouter';
import type { inferRouterOutputs } from '@trpc/server';

type RouterOutputs = inferRouterOutputs<AppRouter>;
export type TradeLogicExecutionJournalRecord = RouterOutputs['strategy']['fetchTradeLogicExecutionJournals']['journals'][number];
type TradeLogicExecutionJournalPayload = TradeLogicExecutionJournalRecord['payload'];
type TradeLogicExecutionJournalPayloadObject = { [key: string]: TradeLogicExecutionJournalPayload };

const isPayloadObject = (value: TradeLogicExecutionJournalPayload): value is TradeLogicExecutionJournalPayloadObject => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const formatPayloadValue = (value: TradeLogicExecutionJournalPayload): string => {
    if (value === null) {
        return 'null';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString();
    }

    return JSON.stringify(value);
};

const getPayloadObjectChild = (payload: TradeLogicExecutionJournalPayload, key: string): TradeLogicExecutionJournalPayloadObject | null => {
    if (!isPayloadObject(payload)) {
        return null;
    }

    const child = payload[key];

    if (!isPayloadObject(child)) {
        return null;
    }

    return child;
};

const getPayloadChildValue = (payload: TradeLogicExecutionJournalPayload, key: string): TradeLogicExecutionJournalPayload | null => {
    if (!isPayloadObject(payload)) {
        return null;
    }

    return payload[key] ?? null;
};

const buildPayloadSummaryRows = (journal: TradeLogicExecutionJournalRecord): { label: string; value: string }[] => {
    const rows: { label: string; value: string }[] = [];
    const outcome = getPayloadChildValue(journal.payload, 'outcome');
    const suppressionReason = getPayloadChildValue(journal.payload, 'suppressionReason');
    const suppressedByCandidateId = getPayloadChildValue(journal.payload, 'suppressedByCandidateId');
    const didSucceed = getPayloadChildValue(journal.payload, 'didSucceed');
    const details = getPayloadObjectChild(journal.payload, 'details');
    const entry = getPayloadObjectChild(journal.payload, 'entry');

    if (outcome !== null) {
        rows.push({ label: 'Outcome', value: formatPayloadValue(outcome) });
    }

    if (didSucceed !== null) {
        rows.push({ label: 'Succeeded', value: formatPayloadValue(didSucceed) });
    }

    if (suppressionReason !== null) {
        rows.push({ label: 'Suppression reason', value: formatPayloadValue(suppressionReason) });
    }

    if (suppressedByCandidateId !== null) {
        rows.push({ label: 'Suppressed by', value: formatPayloadValue(suppressedByCandidateId) });
    }

    if (details?.reason !== undefined && details.reason !== null) {
        rows.push({ label: 'Execution reason', value: formatPayloadValue(details.reason) });
    }

    if (entry?.triggerSource !== undefined && entry.triggerSource !== null) {
        rows.push({ label: 'Trigger source', value: formatPayloadValue(entry.triggerSource) });
    }

    if (entry?.symbol !== undefined && entry.symbol !== null) {
        rows.push({ label: 'Entry symbol', value: formatPayloadValue(entry.symbol) });
    }

    if (journal.row.entryLogId !== null) {
        rows.push({ label: 'Entry log', value: `#${journal.row.entryLogId}` });
    }

    return rows;
};

const TradeLogicExecutionJournalTrace: React.FC<{
    journal: TradeLogicExecutionJournalRecord;
}> = ({ journal }) => {
    const actionPayload = getPayloadObjectChild(journal.payload, 'action');
    const entryPayload = getPayloadObjectChild(journal.payload, 'entry');
    const detailsPayload = getPayloadObjectChild(journal.payload, 'details');
    const payloadSummaryRows = buildPayloadSummaryRows(journal);
    const prettyPayloadJson = JSON.stringify(journal.payload, null, 2);

    return (
        <div className="space-y-3">
            {payloadSummaryRows.length > 0 && (
                <div>
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Trace summary</div>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {payloadSummaryRows.map((row) => (
                            <div className="rounded-md border border-zinc-800 bg-zinc-950/80 px-3 py-2" key={row.label}>
                                <div className="text-[11px] uppercase tracking-wide text-zinc-500">{row.label}</div>
                                <div className="mt-1 break-words text-sm text-zinc-100">{row.value}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                {entryPayload && (
                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Entry context</div>
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words text-xs text-zinc-200">
                            {JSON.stringify(entryPayload, null, 2)}
                        </pre>
                    </div>
                )}
                {actionPayload && (
                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Action snapshot</div>
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words text-xs text-zinc-200">
                            {JSON.stringify(actionPayload, null, 2)}
                        </pre>
                    </div>
                )}
                {detailsPayload && (
                    <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Execution details</div>
                        <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-words text-xs text-zinc-200">
                            {JSON.stringify(detailsPayload, null, 2)}
                        </pre>
                    </div>
                )}
            </div>
            <div className="rounded-md border border-zinc-800 bg-zinc-950/80 p-3">
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">Raw payload</div>
                <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words text-xs text-zinc-200">{prettyPayloadJson}</pre>
            </div>
        </div>
    );
};

export default TradeLogicExecutionJournalTrace;
